import { asSiteCreateInitialView, type SiteCreateInitialView } from "@carnet/ui/utils/siteCreate";

type Step = 1 | 2 | 3 | null;

const STEP_QUERY_KEY = "createSite";
const SITE_ID_QUERY_KEY = "newSiteId";

function parseStep(raw: unknown): Step {
  if (raw === "step1") return 1;
  if (raw === "step2") return 2;
  if (raw === "step3") return 3;
  return null;
}

export function useSiteCreateModal() {
  const route = useRoute();
  const router = useRouter();

  const step = computed<Step>(() => parseStep(route.query[STEP_QUERY_KEY]));
  const isOpen = computed(() => step.value !== null);

  // id du site fraîchement créé, porté par l'URL pour l'étape de succès (step3).
  const createdSiteId = computed<number | null>(() => {
    const raw = route.query[SITE_ID_QUERY_KEY];
    const id = Number(raw);
    return raw != null && Number.isFinite(id) ? id : null;
  });

  // Survit à close() (≠ createdSiteId lié à l'URL) : le flux appelant le met en évidence sur la carte.
  const confirmedSiteId = useState<number | null>("siteCreate:confirmedSiteId", () => null);

  // Incrémenté à chaque création : les cartes de sites l'observent pour recharger leurs données.
  const siteCreatedTick = useState<number>("siteCreate:createdTick", () => 0);

  // Cadrage de départ de l'étape position, repris de la carte appelante (cf. WebSiteCreateLocationStep).
  const initialView = useState<SiteCreateInitialView | null>("siteCreate:initialView", () => null);

  const pushStep = async (value: "step1" | "step2" | "step3", opts?: { replace?: boolean }): Promise<void> => {
    const target = { query: { ...route.query, [STEP_QUERY_KEY]: value } };
    if (opts?.replace) await router.replace(target);
    else await router.push(target);
  };

  // `initial` typé `unknown` : tolère un `@click="open"` qui passe l'événement souris (neutralisé).
  const open = async (initial?: unknown): Promise<void> => {
    initialView.value = asSiteCreateInitialView(initial);
    await pushStep("step1");
  };
  const goStep1 = async (opts?: { replace?: boolean }): Promise<void> => {
    await pushStep("step1", opts);
  };
  const goStep2 = async (opts?: { replace?: boolean }): Promise<void> => {
    await pushStep("step2", opts);
  };
  const goStep3 = async (siteId?: number | null): Promise<void> => {
    siteCreatedTick.value++;
    await router.push({
      query: {
        ...route.query,
        [STEP_QUERY_KEY]: "step3",
        ...(siteId != null ? { [SITE_ID_QUERY_KEY]: String(siteId) } : {}),
      },
    });
  };
  const close = async (): Promise<void> => {
    const { [STEP_QUERY_KEY]: _omitStep, [SITE_ID_QUERY_KEY]: _omitId, ...rest } = route.query;
    await router.push({ query: rest });
  };

  // Ferme le tunnel en signalant le site créé : `confirmedSiteId` survit à la fermeture pour que le
  // flux appelant (création d'activité) le mette en évidence sur la carte. Cf. activities/create/site.vue.
  const confirmAndClose = async (): Promise<void> => {
    confirmedSiteId.value = createdSiteId.value;
    await close();
  };

  return { step, isOpen, createdSiteId, confirmedSiteId, siteCreatedTick, initialView, open, goStep1, goStep2, goStep3, close, confirmAndClose };
}
